<template>
  <div class="third-step">
    <h4 class="text-dark-darken">{{ title }}</h4>
    <div class="delivery-details mt-4">
      <div class="d-flex align-items-center justify-content-start mb-4">
        <small class="qtd-label text-transition font-weight-bold mr-2 d-block flex-1">
          {{ $t('form.labels.itemQuantity') }}:
        </small>
        <b-form-spinbutton
          size="sm"
          id="q-vol"
          class="q-vol"
          v-model="q_vol"
          min="1"
          :max="maxVolumes"
          variant="primary"
          @input="changeValue"
        ></b-form-spinbutton>
      </div>
      <div class="d-flex flex-column" v-if="q_vol > 0">
        <span class="boxsizing-text text-uppercase text-dark-info d-block mb-3">
          {{ $t('form.labels.volumeDetail') }}:
        </span>
        <div v-for="volumeDetail in volumeDetails" :key="volumeDetail.id">
          <div v-if="volumeDetail.show">
            <form @submit.prevent="() => {}">
              <b-row class="mx-0">
                <b-col cols="12" class="px-0">
                  <b-form-group
                    :label="`${$t('form.labels.volumeLabel')} ${volumeDetail.id}`"
                    label-for="identifiers"
                    size="sm"
                  >
                    <b-form-input
                      :placeholder="`${$t('form.labels.volumeLabel')} ${volumeDetail.id}`"
                      size="sm"
                      id="identifiers"
                      v-model="volumeDetail.label"
                      v-on:blur="checkVolumeDetails"
                      type="text"
                      required
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="mx-0">
                <b-col cols="7" class="px-0">
                  <b-form-group
                    :label="`${$t('form.labels.volumeSize')} ${volumeDetail.id}`"
                    label-for="volume-size"
                    :description="`${sizeTextFormatter(volumeDetail.size)}cm`"
                    class="mb-0 volume-label"
                  >
                    <b-form-spinbutton
                      size="sm"
                      id="volume-size"
                      v-model="volumeDetail.size"
                      v-on:change="checkVolumeDetails"
                      min="0"
                      max="2"
                      wrap
                      :formatter-fn="sizeFormatter"
                    ></b-form-spinbutton>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="5"
                  class="px-0 d-flex justify-content-center align-items-center flex-column"
                >
                  <transition name="slide-top-package" mode="out-in">
                    <svg xmlns="http://www.w3.org/2000/svg" width="44.062" height="40.766" viewBox="0 0 44.062 40.766" v-if="volumeDetail.size === 0" v-bind:svg-inline="''" v-bind:class="'package'" v-bind:key="'small-package'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M44.033 13.5v-.013a.756.756 0 00-.035-.1l-.007-.015q-.019-.039-.042-.077l-.006-.011-7.725-12.103A2.542 2.542 0 0034.065 0H10a2.542 2.542 0 00-2.156 1.181L.123 13.293l-.006.007a.73.73 0 00-.042.077l-.007.023a.923.923 0 00-.035.1v.013a.7.7 0 00-.018.085v.024a.87.87 0 00-.007.1v24.5a2.557 2.557 0 002.553 2.553h38.948a2.556 2.556 0 002.553-2.553v-24.5a.783.783 0 00-.007-.1v-.024a.721.721 0 00-.022-.098zM34.9 2.022l6.954 10.909h-19.04V1.565h11.251a.983.983 0 01.835.457zM16.523 14.5h11.016v9.61l-.743-.792a.783.783 0 00-1.142 0l-1.525 1.627-1.529-1.631a.782.782 0 00-1.142 0l-1.526 1.628-1.526-1.628a.783.783 0 00-1.142 0l-.744.793zM9.164 2.022A.983.983 0 0110 1.565h11.248v11.366H2.21zM41.509 39.2H2.553a.989.989 0 01-.988-.988V14.5h13.392v11.586a.783.783 0 001.354.536l1.526-1.628 1.526 1.628a.783.783 0 001.142 0l1.526-1.628 1.526 1.628a.783.783 0 001.142 0l1.525-1.627 1.526 1.627a.783.783 0 001.354-.536V14.5H42.5v23.712a.989.989 0 01-.991.988zm0 0" class="prefix__cls-1" data-name="Path 337" fill="#0b4e6c"/><path d="M11.966 30.925h-6.71a.783.783 0 00-.779.78v3.8a.783.783 0 00.783.783h6.71a.783.783 0 00.783-.783v-3.8a.783.783 0 00-.787-.78zm-.783 3.8H6.039v-2.237h5.144zm0 0" class="prefix__cls-1" data-name="Path 338" fill="#0b4e6c"/><path d="M25.945 30.925H15.768a.783.783 0 100 1.566h10.177a.783.783 0 000-1.566zm0 0" class="prefix__cls-1" data-name="Path 339" fill="#0b4e6c"/><path d="M20.912 33.832h-5.144a.783.783 0 100 1.566h5.144a.783.783 0 000-1.566zm0 0" class="prefix__cls-1" data-name="Path 340" fill="#0b4e6c"/></svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="48.311" height="51.539" viewBox="0 0 48.311 51.539" v-if="volumeDetail.size === 1" v-bind:svg-inline="''" v-bind:class="'package'" v-bind:key="'medium-package'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g data-name="Group 359"><g data-name="Group 358"><path d="M47.885 11.372L24.484.076a.755.755 0 00-.656 0l-23.4 11.3a.755.755 0 00-.427.68v27.432a.755.755 0 00.427.68l23.4 11.3a.755.755 0 00.657 0l23.4-11.3a.755.755 0 00.427-.68V12.052a.755.755 0 00-.427-.68zM24.156 1.594L45.82 12.052l-6.28 3.032a.748.748 0 00-.128-.083L17.895 4.616zM16.19 5.47l21.633 10.443-4.431 2.139L11.768 7.613zm22.139 11.875v7.907l-4.139 2v-7.909zm8.473 21.669L24.911 49.582V23.823l5.221-2.522a.755.755 0 10-.657-1.36l-5.32 2.568-2.093-1.008a.755.755 0 00-.657 1.36l1.995.963v25.758L1.51 39.014V13.255l16.67 8.046a.755.755 0 00.656-1.36l-16.345-7.89 7.5-3.621 22.677 10.95.01.013v9.059a.755.755 0 001.083.68l5.651-2.731a.755.755 0 00.427-.68v-9.104l6.963-3.361v25.758z" class="prefix__cls-1" data-name="Path 334" fill="#0b4e6c"/></g></g><g data-name="Group 361"><g data-name="Group 360"><path d="M7.741 36.086l-3.434-1.658a.755.755 0 10-.657 1.36l3.434 1.658a.755.755 0 00.657-1.36z" class="prefix__cls-1" data-name="Path 335" fill="#0b4e6c"/></g></g><g data-name="Group 363"><g data-name="Group 362"><path d="M10.901 34.028l-6.59-3.181a.755.755 0 10-.656 1.36l6.59 3.181a.755.755 0 00.656-1.36z" class="prefix__cls-1" data-name="Path 336" fill="#0b4e6c"/></g></g></svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="62.641" height="57.668" viewBox="0 0 62.641 57.668" v-if="volumeDetail.size === 2" v-bind:svg-inline="''" v-bind:class="'package'" v-bind:key="'large-package'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g data-name="trolley (1)"><g data-name="Group 374"><g data-name="Group 373"><path d="M33.267 46.579a3.327 3.327 0 103.327 3.327 3.331 3.331 0 00-3.327-3.327zm0 4.435a1.109 1.109 0 111.109-1.109 1.109 1.109 0 01-1.109 1.109z" class="prefix__cls-1" data-name="Path 390" fill="#0b4e6c"/></g></g><g data-name="Group 376"><g data-name="Group 375"><path d="M33.266 42.144a7.762 7.762 0 107.762 7.762 7.771 7.771 0 00-7.762-7.762zm0 13.306a5.544 5.544 0 115.543-5.54 5.551 5.551 0 01-5.543 5.54z" class="prefix__cls-1" data-name="Path 391" fill="#0b4e6c"/></g></g><g data-name="Group 378"><g data-name="Group 377"><path d="M25.504 31.051h2.218v18.851h-2.218z" class="prefix__cls-1" data-name="Rectangle 875" fill="#0b4e6c"/></g></g><g data-name="Group 380"><g data-name="Group 379"><path d="M13.318 4.025A5.539 5.539 0 008.45 1.114H0v2.218h8.447a3.322 3.322 0 012.919 1.748l20.924 38.7 1.952-1.056z" class="prefix__cls-1" data-name="Path 392" fill="#0b4e6c"/></g></g><g data-name="Group 382"><g data-name="Group 381"><path d="M39.389 50.05L61.56 37.84l1.07 1.942-22.172 12.21z" class="prefix__cls-1" data-name="Rectangle 876" fill="#0b4e6c"/></g></g><g data-name="Group 384"><g data-name="Group 383"><path d="M48.646 18.31L38.664.567a1.109 1.109 0 00-1.51-.424l-17.743 9.982a1.109 1.109 0 00-.423 1.51l9.982 17.744a1.106 1.106 0 00.668.525 1.151 1.151 0 00.3.041 1.1 1.1 0 00.543-.144l17.743-9.981a1.108 1.108 0 00.422-1.51zm-18.287 9.016l-8.894-15.811 15.811-8.9 8.893 15.813z" class="prefix__cls-1" data-name="Path 393" fill="#0b4e6c"/></g></g><g data-name="Group 386"><g data-name="Group 385"><path d="M35.151 8.865l-2.773-4.8a1.109 1.109 0 00-1.515-.406l-4.8 2.772a1.109 1.109 0 00-.406 1.515l2.772 4.8a1.109 1.109 0 001.515.406l4.8-2.772a1.109 1.109 0 00.406-1.515zm-5.356 1.812l-1.663-2.881 2.881-1.663 1.663 2.88z" class="prefix__cls-1" data-name="Path 394" fill="#0b4e6c"/></g></g><g data-name="Group 388"><g data-name="Group 387"><path d="M58.622 36.052L48.64 18.309a1.107 1.107 0 00-1.509-.423l-17.743 9.981a1.109 1.109 0 00-.423 1.51l9.982 17.744a1.1 1.1 0 00.668.525 1.145 1.145 0 00.3.041 1.083 1.083 0 00.541-.143L58.2 37.562a1.107 1.107 0 00.422-1.51zm-18.285 9.016l-8.894-15.811 15.811-8.894 8.894 15.811z" class="prefix__cls-1" data-name="Path 395" fill="#0b4e6c"/></g></g><g data-name="Group 390"><g data-name="Group 389"><path d="M45.121 26.616l-2.772-4.8a1.108 1.108 0 00-1.515-.406l-4.8 2.772a1.109 1.109 0 00-.406 1.515l2.772 4.8a1.108 1.108 0 001.515.406l4.8-2.772a1.109 1.109 0 00.406-1.515zm-5.356 1.812l-1.663-2.881 2.881-1.663 1.663 2.881z" class="prefix__cls-1" data-name="Path 396" fill="#0b4e6c"/></g></g></g></svg>
                  </transition>
                </b-col>
              </b-row>
              <b-row class="mx-0">
                <b-col cols="12" class="px-0">
                  <b-form-group
                    :label="`${$t('form.labels.weight')} ${volumeDetail.id}`"
                    label-for="weight"
                    size="sm"
                  >
                    <b-form-input
                      :placeholder="`${$t('form.placeholders.weight')} ${volumeDetail.id} (kg)`"
                      size="sm"
                      id="weight"
                      v-model="volumeDetail.weight"
                      v-on:blur="checkVolumeDetails"
                      type="text"
                      required
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </form>
          </div>
          <hr v-if="volumeDetail.show" />
        </div>
        <b-alert show variant="secondary" class="p-2 mt-2">
          <small>
            <em>{{ $t('form.describeTexts.identifier') }}</em>
          </small>
        </b-alert>
      </div>
    </div>
  </div>
</template>

<script>
import { safeStorage } from '@/utils';

export default {
  name: 'third-step',
  props: {
    title: {
      type: String,
      required: true,
    },
    volumeInfo: {
      type: Object,
      required: true,
    },
    packSize: {
      type: String,
      required: true,
    },
    nfeKey: {
      type: String,
      required: false,
    },
    identifiers: {
      type: Array,
      required: false,
    },
    boxSizes: {
      type: Array,
      required: false,
    },
  },
  mounted() {
    this.q_vol = this.volumeInfo.q_vol === 0 ? 1 : this.volumeInfo.q_vol;
    this.volume = this.volumeInfo.volume;
    this.packageSize = this.packSize;
    const { identifiers } = this;
    const { boxSizes } = this;
    const { weight } = this;

    for (let item = 1; item <= this.maxVolumes; item += 1) {
      const identifier = identifiers.shift();
      const boxSize = boxSizes.shift();

      this.volumeDetails.push({
        show: false,
        id: item,
        label: identifier === undefined ? '' : identifier,
        size: boxSize === undefined ? 0 : boxSize,
        weight: weight === undefined ? 0 : weight,
        sizeDescription: '',
      });
    }

    this.volumeDetails[0].label =
      this.volumeDetails[0].label === '' ? this.nfeKey : this.volumeDetails[0].label;
    this.checkVolumeDetails();
  },
  data() {
    return {
      packageSize: '1',
      q_vol: 0,
      volume: 0,
      weight: null,
      hexTokens: {
        F: {
          pattern: /[0-3]/,
          transform: v => v.tolaleUpperCase(),
        },
      },
      volumeDetails: [],
      sizes: [
        this.$t('form.labels.small'),
        this.$t('form.labels.medium'),
        this.$t('form.labels.large'),
      ],
      maxVolumes: 10,
    };
  },
  watch: {
    packageSize(val, oldVal) {
      const { spot_weight } = safeStorage.getItem('config');

      switch (val) {
        case '1':
          this.volume = spot_weight.small.volume;
          break;
        case '2':
          this.volume = spot_weight.medium.volume;
          break;
        case '3':
          this.volume = spot_weight.large.volume;
          break;
        default:
          this.volume = spot_weight.small.volume;
      }

      if (val !== oldVal) {
        this.changeValue();
      }
    },
  },
  computed: {
    sizeText() {
      let text = '';

      switch (this.packageSize) {
        case '1':
          text = 'smallBoxSize';
          break;
        case '2':
          text = 'mediumBoxSize';
          break;
        case '3':
          text = 'largeBoxSize';
          break;
        default:
          text = 'smallBoxSize';
      }

      return this.$t(`spot.${text}`);
    },
    boxSize() {
      const { spot_weight } = safeStorage.getItem('config');

      switch (this.packageSize) {
        case '1':
          return spot_weight.small.size;
        case '2':
          return spot_weight.medium.size;
        case '3':
          return spot_weight.large.size;
        default:
          return spot_weight.small.size;
      }
    },
  },
  methods: {
    getVolume(value) {
      const { spot_weight } = safeStorage.getItem('config');

      switch (value) {
        case 0:
          return parseFloat(spot_weight.small.volume);
        case 1:
          return parseFloat(spot_weight.medium.volume);
        case 2:
          return parseFloat(spot_weight.large.volume);
        default:
          return parseFloat(spot_weight.small.volume);
      }
    },
    sizeTextFormatter(value) {
      const { spot_weight } = safeStorage.getItem('config');

      switch (value) {
        case 0:
          return spot_weight.small.size;
        case 1:
          return spot_weight.medium.size;
        case 2:
          return spot_weight.large.size;
        default:
          return spot_weight.small.size;
      }
    },
    sizeFormatter(value) {
      return this.sizes[value];
    },
    changeValue() {
      this.volumeDetails = this.volumeDetails.map(item => {
        item.show = item.id <= this.q_vol;

        return item;
      });

      this.checkVolumeDetails();
    },
    checkVolumeDetails() {
      let volume = 0;
      let weightTotal = 0;
      const done = this.volumeDetails[0].label.length > 0;
      let maxSize = 0;

      this.volumeDetails.forEach(item => {
        if (item.show) {
          volume += this.getVolume(item.size);

          if (maxSize < item.size) {
            maxSize = item.size;
          }
          weightTotal += Number(item.weight);
        }
      });

      if (done) {
        this.packageSize = `${maxSize + 1}`;
        this.$emit('next-step', {
          q_vol: Number(this.q_vol),
          volume: Number(volume.toFixed(3)),
          identifiers: this.volumeDetails.filter(v => v.show).map(v => v.label),
          weight: weightTotal,
          weights: this.volumeDetails.filter(v => v.show).map(v => v.weight),
          sizes: this.volumeDetails.filter(v => v.show).map(v => v.size),
          volumes: this.volumeDetails.filter(v => v.show).map(v => this.getVolume(v.size)),
          sizesCount: this.countBoxSizes(),
        });
      }
    },
    getImagePath() {
      let packageSize = '';

      switch (this.packageSize) {
        case '1':
          packageSize = 'small';
          break;
        case '2':
          packageSize = 'medium';
          break;
        case '3':
          packageSize = 'large';
          break;
        default:
          packageSize = 'small';
      }

      return packageSize;
    },
    countBoxSizes() {
      const boxes = [
        { count: 0, text: `${this.sizeTextFormatter(0)}cm`, image: 'small' },
        { count: 0, text: `${this.sizeTextFormatter(1)}cm`, image: 'medium' },
        { count: 0, text: `${this.sizeTextFormatter(2)}cm`, image: 'large' },
      ];

      this.volumeDetails.forEach(item => {
        if (item.show) {
          boxes[item.size].count += 1;
        }
      });

      return boxes;
    },
  },
};
</script>

<style lang="scss">
.volume-label > .bv-no-focus-ring > .form-text {
  text-align: center;
}

.third-step {
  // slide-top-package
  .slide-top-package-enter-active,
  .slide-top-package-leave-active {
    transition: all 200ms ease;
  }
  .slide-top-package-enter,
  .slide-top-package-leave-to {
    transform: translateY(-40px);
    opacity: 0;
  }

  .q-vol {
    width: 110px;
  }

  .qtd-label {
    white-space: nowrap;
    max-width: fit-content;
  }

  .qtd-input {
    width: 100px;
  }

  .package {
    height: 60px;
  }

  .boxsizing-text {
    font-weight: 600;
  }

  .limit-point {
    content: '';
    height: 26px;
    width: 7px;
    border-radius: 3.5px;
    background: #fff;
    border: 1px solid var(--transition);
  }

  .slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 4px;
    background: var(--transition);
    outline: none;
    -webkit-transition: 0.2s;
    transition: opacity 0.2s;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none !important;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      background: var(--primary);
      cursor: pointer;
      position: relative;
      z-index: 100;
      border: 0;
    }

    &::-moz-range-thumb {
      -moz-appearance: none;
      appearance: none !important;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      background: var(--primary);
      cursor: pointer;
      position: relative;
      z-index: 100;
      border: 0;
    }
  }
}
</style>
